/**
 * Phase F4 — Admin Audit Log Center
 * ----------------------------------
 * Surfaces /api/audit-logs with filters.
 *
 * Endpoints used:
 *   GET /api/audit-logs?entity_type=…&entity_id=…&actor_id=…&action=…&limit=…
 *   GET /api/audit-logs/{log_id}    (drill-down)
 *
 * Pure UI. No backend logic modified.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ScrollText, Search, X, Filter } from 'lucide-react';
import theme from '../theme';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const cardStyle = {
  background: theme.colors.bg.card, border: `1px solid ${theme.colors.borders}`,
  borderRadius: theme.radius.card, boxShadow: theme.colors.shadows.sm,
};

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function AdminAuditLogCenter() {
  const [filters, setFilters] = useState({ entity_type: '', entity_id: '', actor_id: '', action: '' });
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawer, setDrawer] = useState(null);

  const auth = useMemo(() => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  }), []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '200' });
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      const res = await axios.get(`${API}/audit-logs?${params.toString()}`, auth);
      setRows(res.data?.logs || []);
    } catch (e) {
      toast.error('Failed to load audit logs');
    } finally { setLoading(false); }
  }, [filters, auth]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter((r) =>
      (r.action || '').toLowerCase().includes(q) ||
      (r.entity_type || '').toLowerCase().includes(q) ||
      (r.entity_id || '').toLowerCase().includes(q) ||
      (r.actor_id || '').toLowerCase().includes(q) ||
      (r.actor_email || '').toLowerCase().includes(q)
    );
  }, [rows, search]);

  return (
    <div data-testid="admin-audit-log-page" style={{ padding: 24, maxWidth: 1400, margin: '0 auto' }}>
      <div style={{ marginBottom: 18 }}>
        <h1 style={{
          fontSize: 28, fontWeight: 800, color: theme.colors.text.primary, margin: 0,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <ScrollText size={26} color={theme.colors.primary} />
          Audit Log Center
        </h1>
        <p style={{ color: theme.colors.text.secondary, marginTop: 4 }}>
          Full audit history of every entity. Filter by action, entity, or actor.
        </p>
      </div>

      <div style={{ ...cardStyle, padding: 16, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <Filter size={14} color={theme.colors.primary} />
          <div style={{ fontWeight: 700, fontSize: 13 }}>Filters</div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
          {['entity_type', 'entity_id', 'actor_id', 'action'].map((k) => (
            <input
              key={k}
              data-testid={`audit-filter-${k}`}
              placeholder={k.replace('_', ' ')}
              value={filters[k]}
              onChange={(e) => setFilters({ ...filters, [k]: e.target.value })}
              style={{
                padding: '8px 10px', borderRadius: theme.radius.sm,
                border: `1px solid ${theme.colors.borders}`, fontSize: 12,
                outline: 'none', boxSizing: 'border-box',
              }}
            />
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
          <Search size={14} color={theme.colors.text.muted} />
          <input
            data-testid="audit-quick-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Quick search across loaded results…"
            style={{
              flex: 1, padding: '8px 10px', borderRadius: theme.radius.sm,
              border: `1px solid ${theme.colors.borders}`, fontSize: 12, outline: 'none',
            }}
          />
        </div>
      </div>

      {loading ? (
        <div data-testid="audit-loading" style={{ ...cardStyle, padding: 40, textAlign: 'center', color: theme.colors.text.muted }}>
          Loading…
        </div>
      ) : filtered.length === 0 ? (
        <div data-testid="audit-empty" style={{ ...cardStyle, padding: 40, textAlign: 'center', color: theme.colors.text.secondary }}>
          No audit log entries match these filters.
        </div>
      ) : (
        <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead style={{ background: theme.colors.bg.secondary }}>
              <tr>
                {['Timestamp', 'Action', 'Entity Type', 'Entity ID', 'Actor', 'Role', ''].map((h) => (
                  <th key={h} style={{
                    textAlign: 'left', padding: '10px 12px',
                    fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5,
                    color: theme.colors.text.muted, fontWeight: 700,
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} data-testid={`audit-row-${r.id}`} style={{ borderTop: `1px solid ${theme.colors.borders}` }}>
                  <td style={{ padding: '8px 12px', color: theme.colors.text.secondary, fontSize: 11 }}>
                    {fmtDate(r.created_at)}
                  </td>
                  <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontWeight: 600, color: theme.colors.text.primary }}>
                    {r.action || '—'}
                  </td>
                  <td style={{ padding: '8px 12px', color: theme.colors.text.secondary }}>{r.entity_type || '—'}</td>
                  <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: 11, color: theme.colors.text.muted }}>
                    {(r.entity_id || '').slice(0, 14) || '—'}
                  </td>
                  <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: 11 }}>
                    {(r.actor_email || r.actor_id || '').slice(0, 16)}
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    <span style={{
                      padding: '1px 6px', borderRadius: 999, fontSize: 10,
                      background: theme.colors.bg.tertiary, color: theme.colors.text.secondary,
                      fontWeight: 700,
                    }}>{r.actor_role || '—'}</span>
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    <button
                      data-testid={`audit-view-${r.id}`}
                      onClick={() => setDrawer(r)}
                      style={{
                        padding: '4px 10px', borderRadius: theme.radius.sm,
                        border: `1px solid ${theme.colors.borders}`,
                        background: theme.colors.bg.card, cursor: 'pointer',
                        fontSize: 11, fontWeight: 700, color: theme.colors.primary,
                      }}
                    >View</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {drawer && (
        <div data-testid="audit-drawer" style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
          display: 'flex', justifyContent: 'flex-end', zIndex: 100,
        }} onClick={() => setDrawer(null)}>
          <div onClick={(e) => e.stopPropagation()} style={{
            width: 'min(560px, 100%)', height: '100%', background: theme.colors.bg.card,
            padding: 24, overflowY: 'auto',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14 }}>
              <div style={{ fontWeight: 800, fontSize: 18 }}>Audit Entry</div>
              <button onClick={() => setDrawer(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={20} />
              </button>
            </div>
            <pre style={{
              padding: 12, background: theme.colors.bg.secondary,
              borderRadius: theme.radius.sm, fontSize: 12,
              overflow: 'auto', maxWidth: '100%', whiteSpace: 'pre-wrap',
              fontFamily: 'monospace',
            }}>
              {JSON.stringify(drawer, null, 2)}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}
